<script setup lang="ts">
import { navigateTo } from "#imports";
import { buildAmazzingFilterGroupState } from "./amazzingFilterState";
import type { FlattenedAmazzingValue } from "./amazzingFilterState";
import {
  mergeFilterTargetUrl,
  mergeMultiSelectTargetUrl,
} from "./amazzingFilterUrl";
import type {
  AmazzingAppliedFilter,
  AmazzingFilterGroup,
  AmazzingFilterValue,
  AmazzingListingFiltersMeta,
  StoreListingResult,
} from "~~/shared/types/store";
import { useUIStore } from "~~/stores/uiStore";

const props = withDefaults(
  defineProps<{
    listing?: StoreListingResult | null;
    filters?: AmazzingFilterGroup[];
    filtersMeta?: AmazzingListingFiltersMeta | null;
    isInitialLoading?: boolean;
    isUpdating?: boolean;
    canExpandFilters?: boolean;
    canCollapseFilters?: boolean;
    isExpandingFilters?: boolean;
    expandFiltersError?: string | null;
    mobileModalFilters?: boolean;
  }>(),
  {
    listing: null,
    filters: undefined,
    filtersMeta: null,
    isInitialLoading: false,
    isUpdating: false,
    canExpandFilters: false,
    canCollapseFilters: false,
    isExpandingFilters: false,
    expandFiltersError: null,
    mobileModalFilters: false,
  },
);
const emit = defineEmits<{
  "expand-filters": [];
  "collapse-filters": [];
}>();

const INITIAL = 5;
const SEARCH_THRESHOLD = 12;
const AMAZZING_PLACEHOLDER = "__RANGE__";

function isAmazzingSliderGroup(group: AmazzingFilterGroup) {
  return group.controlType === "slider" && Boolean(group.range);
}

function normalizeFilterSearch(value: string) {
  return value.trim().toLocaleLowerCase("pl-PL");
}

function getGroupKey(group: AmazzingFilterGroup) {
  return `amazzing:${group.id}`;
}

function clampPrice(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function getRangeTrackStyle(
  min: number,
  max: number,
  from: number,
  to: number,
) {
  const span = Math.max(max - min, 1);
  const start = ((clampPrice(from, min, max) - min) / span) * 100;
  const end = ((clampPrice(to, min, max) - min) / span) * 100;

  return {
    "--range-start": `${Math.min(start, end)}%`,
    "--range-end": `${Math.max(start, end)}%`,
  };
}

const amazzingFilters = computed(() =>
  (props.filters ?? props.listing?.filters ?? []).slice(),
);

function collectSelectedFilterValues(
  group: AmazzingFilterGroup,
  values: AmazzingFilterValue[] | undefined,
  out: AmazzingAppliedFilter[],
) {
  for (const value of values ?? []) {
    if (value.selected && value.targetUrl) {
      out.push({
        groupId: group.id,
        valueId: value.id,
        label: `${group.name}: ${value.label}`,
        targetUrl: value.targetUrl,
      });
    }
    if (value.children?.length) {
      collectSelectedFilterValues(group, value.children, out);
    }
  }
}

function isAmazzingRangeActive(group: AmazzingFilterGroup) {
  if (!group.range) return false;
  const { from, to, min, max } = group.range;
  return from !== min || to !== max;
}

const amazzingAppliedFilters = computed<AmazzingAppliedFilter[]>(() => {
  const fromListing = props.listing?.appliedFilters ?? [];
  let result: AmazzingAppliedFilter[];

  if (fromListing.length > 0) {
    result = fromListing;
  }
  else {
    const pills: AmazzingAppliedFilter[] = [];
    for (const group of amazzingFilters.value) {
      collectSelectedFilterValues(group, group.values, pills);
      if (
        isAmazzingSliderGroup(group)
        && group.range
        && isAmazzingRangeActive(group)
        && group.resetTargetUrl
      ) {
        const { from, to, unit } = group.range;
        pills.push({
          groupId: group.id,
          valueId: `${group.id}:range`,
          label: `${group.name}: ${from}–${to} ${unit}`,
          targetUrl: group.resetTargetUrl,
        });
      }
    }
    result = pills;
  }

  // Hide category tree pills on category listing — current category is the page context, not a removable filter.
  if (props.listing?.listingType === 'category') {
    const treeGroupIds = new Set(
      amazzingFilters.value
        .filter(g => g.controlType === 'tree')
        .map(g => g.id),
    );
    if (treeGroupIds.size > 0) {
      result = result.filter(p => !treeGroupIds.has(p.groupId));
    }
  }

  return result;
});

// The mobile modal renders pending-aware applied-filter pills so a pill
// disappears the instant its × is tapped (and reappears if re-selected), even
// though listing.appliedFilters only refreshes after "Zastosuj". It OVERLAYS
// the queued selection state onto the authoritative amazzingAppliedFilters
// (rather than rebuilding from facet values) so a server-applied filter is
// never dropped — facet groups can be truncated (filtersMeta.truncated), so a
// rebuild would hide an active filter whose group isn't loaded. Desktop keeps
// using amazzingAppliedFilters directly (it navigates immediately, no pending).
interface MobileAppliedPill {
  key: string;
  label: string;
  groupId: string;
  valueId: string;
  // Backend removal URL — used when the pill has no loaded facet value
  // (truncated facet or a price-range pill).
  fallbackTargetUrl: string;
  // Resolved facet value when present in the loaded filters; lets removal reuse
  // the value-toggle path (keeps the comma multi-select format).
  group: AmazzingFilterGroup | null;
  value: FlattenedAmazzingValue | null;
}

function findFacetValue(groupId: string, valueId: string) {
  const group = amazzingFilters.value.find((entry) => entry.id === groupId);
  if (!group) {
    return null;
  }
  const value = (
    getVisibleAmazzingValues(group) as FlattenedAmazzingValue[]
  ).find((entry) => entry.id === valueId);
  return value ? { group, value } : null;
}

const mobileAppliedFilters = computed<MobileAppliedPill[]>(() => {
  const isCategory = props.listing?.listingType === "category";
  const pills: MobileAppliedPill[] = [];
  const seen = new Set<string>();

  // 1. Authoritative server-applied filters, minus any whose removal is queued.
  for (const applied of amazzingAppliedFilters.value) {
    if (
      mobilePendingSelectedValues.value[applied.groupId]?.[applied.valueId]
      === false
    ) {
      continue;
    }
    const key = `${applied.groupId}:${applied.valueId}`;
    seen.add(key);
    const located = findFacetValue(applied.groupId, applied.valueId);
    pills.push({
      key,
      label: applied.label,
      groupId: applied.groupId,
      valueId: applied.valueId,
      fallbackTargetUrl: applied.targetUrl,
      group: located?.group ?? null,
      value: located?.value ?? null,
    });
  }

  // 2. Values toggled ON in the modal that the server doesn't reflect yet.
  for (const group of amazzingFilters.value) {
    const groupPending = mobilePendingSelectedValues.value[group.id];
    if (!groupPending) {
      continue;
    }
    // Mirror desktop: the current category tree is page context, not a filter.
    if (isCategory && group.controlType === "tree") {
      continue;
    }
    for (const value of getVisibleAmazzingValues(
      group,
    ) as FlattenedAmazzingValue[]) {
      if (groupPending[value.id] !== true || !value.targetUrl) {
        continue;
      }
      const key = `${group.id}:${value.id}`;
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      pills.push({
        key,
        label: `${group.name}: ${value.label}`,
        groupId: group.id,
        valueId: value.id,
        fallbackTargetUrl: value.targetUrl,
        group,
        value,
      });
    }
  }

  return pills;
});

const globalResetUrl = computed(() => {
  const url = props.listing?.currentUrl ?? "";
  const idx = url.indexOf("?");
  return idx >= 0 ? url.slice(0, idx) : url;
});

const hasAmazzingFilters = computed(
  () =>
    amazzingAppliedFilters.value.length > 0 || amazzingFilters.value.length > 0,
);

function shouldShowFilterSearch(totalItems: number) {
  return totalItems >= SEARCH_THRESHOLD;
}

const groupSearchQueries = ref<Record<string, string>>({});

function getGroupSearchQuery(group: AmazzingFilterGroup) {
  return groupSearchQueries.value[getGroupKey(group)] ?? "";
}

function getAmazzingSearchQuery(group: AmazzingFilterGroup) {
  return getGroupSearchQuery(group);
}

function setGroupSearchQuery(group: AmazzingFilterGroup, value: string) {
  groupSearchQueries.value = {
    ...groupSearchQueries.value,
    [getGroupKey(group)]: value,
  };
}

function setAmazzingSearchQuery(group: AmazzingFilterGroup, value: string) {
  setGroupSearchQuery(group, value);
}

function getFilteredAmazzingValues(group: AmazzingFilterGroup) {
  const values = getVisibleAmazzingValues(group);
  const query = normalizeFilterSearch(getAmazzingSearchQuery(group));

  if (!query) {
    return values;
  }

  return values.filter((item) =>
    normalizeFilterSearch(item.label).includes(query),
  );
}

function getRenderedAmazzingValues(group: AmazzingFilterGroup) {
  const values = getFilteredAmazzingValues(group);
  const hasSearch = Boolean(
    normalizeFilterSearch(getAmazzingSearchQuery(group)),
  );

  if (hasSearch || !group.hasMoreToggle) {
    return values;
  }

  return values.slice(0, getAmazzingVisible(group));
}

function hasMoreAmazzingValues(group: AmazzingFilterGroup) {
  if (
    !group.hasMoreToggle ||
    normalizeFilterSearch(getAmazzingSearchQuery(group))
  ) {
    return false;
  }

  return getAmazzingVisible(group) < getVisibleAmazzingValues(group).length;
}

const amazzingGroupStates = computed(
  () =>
    new Map(
      amazzingFilters.value.map((group) => [
        group.id,
        buildAmazzingFilterGroupState(group),
      ]),
    ),
);

function hasSelectedAmazzingValues(group: AmazzingFilterGroup) {
  return amazzingGroupStates.value.get(group.id)?.hasSelection ?? false;
}

function getVisibleAmazzingValues(group: AmazzingFilterGroup) {
  return amazzingGroupStates.value.get(group.id)?.visibleValues ?? [];
}

function shouldRenderAmazzingGroup(group: AmazzingFilterGroup) {
  if (isAmazzingSliderGroup(group)) {
    return true;
  }

  return getVisibleAmazzingValues(group).length > 0;
}

const visibleAmazzingFilters = computed(() =>
  amazzingFilters.value.filter(shouldRenderAmazzingGroup),
);

const isMobileFilterModalOpen = ref(false);

const windowScrollY = ref(0);

function onWindowScroll() {
  windowScrollY.value = window.scrollY;
}
const mobileExpandedFilterGroups = ref<Record<string, boolean>>({});
const mobileFilterScrollY = ref(0);
const mobileModalTopOffset = ref(102);
const mobilePendingTargetUrl = ref<string | null>(null);
const mobilePendingSelectedValues = ref<
  Record<string, Record<string, boolean>>
>({});
const previousBodyOverflow = ref<string | null>(null);

function readMobileModalTopOffset() {
  if (!import.meta.client) {
    return 102;
  }

  const header = document.querySelector<HTMLElement>(".site-header");
  return Math.max(72, Math.ceil(header?.getBoundingClientRect().height ?? 102));
}

function openMobileFilterModal() {
  if (!props.mobileModalFilters || isMobileFilterModalOpen.value) {
    return;
  }

  if (import.meta.client) {
    mobileFilterScrollY.value = window.scrollY;
    mobileModalTopOffset.value = readMobileModalTopOffset();
    previousBodyOverflow.value = document.body.style.overflow;
    document.body.style.overflow = "hidden";
  }

  mobileExpandedFilterGroups.value = {};
  mobilePendingTargetUrl.value = null;
  mobilePendingSelectedValues.value = {};
  isMobileFilterModalOpen.value = true;
}

function isMobileFilterGroupExpanded(group: AmazzingFilterGroup) {
  return Boolean(mobileExpandedFilterGroups.value[group.id]);
}

function toggleMobileFilterGroup(group: AmazzingFilterGroup) {
  mobileExpandedFilterGroups.value = {
    ...mobileExpandedFilterGroups.value,
    [group.id]: !isMobileFilterGroupExpanded(group),
  };
}

function closeMobileFilterModal(restoreScroll = true) {
  if (!isMobileFilterModalOpen.value) {
    return;
  }

  const scrollY = mobileFilterScrollY.value;
  isMobileFilterModalOpen.value = false;
  mobilePendingTargetUrl.value = null;
  mobilePendingSelectedValues.value = {};

  if (!import.meta.client) {
    return;
  }

  if (previousBodyOverflow.value !== null) {
    document.body.style.overflow = previousBodyOverflow.value;
    previousBodyOverflow.value = null;
  }

  if (restoreScroll) {
    window.scrollTo({ top: scrollY, behavior: "auto" });
  }
}

watch(hasAmazzingFilters, (hasFilters) => {
  if (!hasFilters) {
    closeMobileFilterModal(false);
  }
});

watch(
  () => props.mobileModalFilters,
  (enabled) => {
    if (!enabled) {
      closeMobileFilterModal(false);
    }
  },
);

watch(visibleAmazzingFilters, () => {
  if (
    isMobileFilterModalOpen.value &&
    visibleAmazzingFilters.value.length === 0
  ) {
    closeMobileFilterModal(false);
  }
});

onMounted(() => {
  if (import.meta.client) {
    window.addEventListener("scroll", onWindowScroll, { passive: true });
  }
});

onUnmounted(() => {
  if (import.meta.client) {
    window.removeEventListener("scroll", onWindowScroll);
    if (previousBodyOverflow.value !== null) {
      document.body.style.overflow = previousBodyOverflow.value;
    }
  }
});

const showStickyTrigger = computed(
  () => windowScrollY.value > 100 && !isMobileFilterModalOpen.value,
);

function mergeMobileFilterTargetUrl(targetUrl: string) {
  return mergeFilterTargetUrl(
    props.listing?.currentUrl ?? "/",
    mobilePendingTargetUrl.value,
    targetUrl,
  );
}

function isMobileMultiSelectGroup(group: AmazzingFilterGroup) {
  return group.controlType === "checkbox" || group.controlType === "color";
}

function mergeMobileMultiSelectTargetUrl(
  targetUrl: string,
  shouldSelectValue: boolean,
) {
  return mergeMultiSelectTargetUrl(
    props.listing?.currentUrl ?? "/",
    mobilePendingTargetUrl.value,
    targetUrl,
    shouldSelectValue,
  );
}

function queueMobileFilterTarget(targetUrl: string) {
  if (!targetUrl) {
    return;
  }

  mobilePendingTargetUrl.value = mergeMobileFilterTargetUrl(targetUrl);
}

function isMobileFilterValueSelected(
  group: AmazzingFilterGroup,
  value: FlattenedAmazzingValue,
) {
  return (
    mobilePendingSelectedValues.value[group.id]?.[value.id] ?? value.selected
  );
}

function queueMobileFilterValue(
  group: AmazzingFilterGroup,
  value: FlattenedAmazzingValue,
) {
  const currentGroupState = mobilePendingSelectedValues.value[group.id] ?? {};
  const nextGroupState = { ...currentGroupState };
  const currentSelected = isMobileFilterValueSelected(group, value);
  const nextSelected =
    group.controlType === "radio" || group.controlType === "select"
      ? true
      : !currentSelected;

  mobilePendingTargetUrl.value = isMobileMultiSelectGroup(group)
    ? mergeMobileMultiSelectTargetUrl(value.targetUrl, nextSelected)
    : mergeMobileFilterTargetUrl(value.targetUrl);

  if (group.controlType === "radio" || group.controlType === "select") {
    for (const item of getVisibleAmazzingValues(group)) {
      nextGroupState[item.id] = false;
    }
    nextGroupState[value.id] = true;
  } else {
    nextGroupState[value.id] = nextSelected;
  }

  mobilePendingSelectedValues.value = {
    ...mobilePendingSelectedValues.value,
    [group.id]: nextGroupState,
  };
}

function removeMobileAppliedFilter(pill: MobileAppliedPill) {
  // When the facet value is loaded, reuse the value-toggle path so multi-select
  // removals keep the comma format and stay in sync with the group's checkboxes.
  if (pill.group && pill.value) {
    queueMobileFilterValue(pill.group, pill.value);
    return;
  }
  // Otherwise (server-applied filter from a truncated facet, or a price-range
  // pill) queue the backend's removal target and mark the value deselected so
  // the pill disappears immediately.
  queueMobileFilterTarget(pill.fallbackTargetUrl);
  mobilePendingSelectedValues.value = {
    ...mobilePendingSelectedValues.value,
    [pill.groupId]: {
      ...(mobilePendingSelectedValues.value[pill.groupId] ?? {}),
      [pill.valueId]: false,
    },
  };
}

function queueMobileFilterGroupReset(group: AmazzingFilterGroup) {
  queueMobileFilterTarget(group.resetTargetUrl);

  mobilePendingSelectedValues.value = {
    ...mobilePendingSelectedValues.value,
    [group.id]: Object.fromEntries(
      getVisibleAmazzingValues(group).map((value) => [value.id, false]),
    ),
  };
}

function hasMobileSelectedDescendant(
  group: AmazzingFilterGroup,
  value: FlattenedAmazzingValue,
) {
  const allValues = getVisibleAmazzingValues(group) as FlattenedAmazzingValue[];
  const parentMap = new Map(allValues.map((item) => [item.id, item.parentId]));

  return allValues.some((item) => {
    if (!isMobileFilterValueSelected(group, item)) return false;
    let parentId = item.parentId;
    while (parentId) {
      if (parentId === value.id) return true;
      parentId = parentMap.get(parentId);
    }
    return false;
  });
}

function applyMobileFilters() {
  const targetUrl = mobilePendingTargetUrl.value;

  if (!targetUrl) {
    closeMobileFilterModal();
    return;
  }

  mobilePendingTargetUrl.value = null;
  navigateToTarget(targetUrl);
}

// ── Tree expand/collapse ──
const treeExpandedIds = ref<Record<string, Set<string>>>({});

function isGroupTreeLike(group: AmazzingFilterGroup): boolean {
  return (
    group.controlType === "tree" ||
    (getVisibleAmazzingValues(group) as FlattenedAmazzingValue[]).some(
      (v) => v.hasChildren,
    )
  );
}

function isTreeExpanded(
  group: AmazzingFilterGroup,
  value: FlattenedAmazzingValue,
): boolean {
  return treeExpandedIds.value[group.id]?.has(value.id) ?? false;
}

function hasVisibleChildrenInGroup(
  group: AmazzingFilterGroup,
  value: FlattenedAmazzingValue,
): boolean {
  return (getVisibleAmazzingValues(group) as FlattenedAmazzingValue[]).some(
    (v) => v.parentId === value.id,
  );
}

function hasSelectedDescendant(
  group: AmazzingFilterGroup,
  value: FlattenedAmazzingValue,
): boolean {
  const allValues = getVisibleAmazzingValues(group) as FlattenedAmazzingValue[];
  const parentMap = new Map(allValues.map((v) => [v.id, v.parentId]));
  return allValues.some((v) => {
    if (!v.selected) return false;
    let parentId = v.parentId;
    while (parentId) {
      if (parentId === value.id) return true;
      parentId = parentMap.get(parentId);
    }
    return false;
  });
}

function getTreeVisibleValues(
  group: AmazzingFilterGroup,
): FlattenedAmazzingValue[] {
  const values = getVisibleAmazzingValues(group) as FlattenedAmazzingValue[];
  const expanded = treeExpandedIds.value[group.id] ?? new Set();
  const visibleSet = new Set<string>();

  for (const value of values) {
    if (value.depth === 0) {
      visibleSet.add(value.id);
    } else if (
      value.parentId &&
      expanded.has(value.parentId) &&
      visibleSet.has(value.parentId)
    ) {
      visibleSet.add(value.id);
    }
  }

  return values.filter((v) => visibleSet.has(v.id));
}

// Węzły zwinięte ręcznie — auto-rozwijanie zaznaczonych (filterTreeExpandSelected)
// ich nie otwiera ponownie, gdy przyjdzie nowy listing (sortowanie, strona, filtr).
const userCollapsedTreeIds = new Map<string, Set<string>>();

function toggleTreeValue(
  group: AmazzingFilterGroup,
  value: FlattenedAmazzingValue,
): void {
  const current = new Set(treeExpandedIds.value[group.id] ?? []);
  const collapsed = userCollapsedTreeIds.get(group.id) ?? new Set<string>();
  if (current.has(value.id)) {
    current.delete(value.id);
    collapsed.add(value.id);
  } else {
    current.add(value.id);
    collapsed.delete(value.id);
  }
  userCollapsedTreeIds.set(group.id, collapsed);
  treeExpandedIds.value = { ...treeExpandedIds.value, [group.id]: current };
}

function getTreeToggleLabel(
  group: AmazzingFilterGroup,
  value: FlattenedAmazzingValue,
): string {
  return `${isTreeExpanded(group, value) ? "Zwiń" : "Rozwiń"} kategorię ${value.label}`;
}

// Auto-expand parents of selected values on filter change. Marka z
// filterTreeExpandSelected (proles) rozwija też sam zaznaczony węzeł — na
// stronie działu widać od razu jego podkategorie.
const expandSelectedTreeValues = useBrand().filterTreeExpandSelected === true;
watch(
  amazzingFilters,
  () => {
    const next: Record<string, Set<string>> = {};
    for (const group of amazzingFilters.value) {
      const values = getVisibleAmazzingValues(
        group,
      ) as FlattenedAmazzingValue[];
      const expanded = new Set(treeExpandedIds.value[group.id] ?? []);
      const parentById = new Map(
        values.map((value) => [value.id, value.parentId]),
      );
      for (const value of values) {
        if (
          expandSelectedTreeValues &&
          value.selected &&
          !userCollapsedTreeIds.get(group.id)?.has(value.id)
        ) {
          expanded.add(value.id);
        }
        let parentId = value.selected ? value.parentId : undefined;
        while (parentId) {
          expanded.add(parentId);
          parentId = parentById.get(parentId);
        }
      }
      next[group.id] = expanded;
    }
    treeExpandedIds.value = next;
  },
  { immediate: true },
);

// „Zobacz wszystkie kategorie” z mobilnego menu: drawer zapisuje w uiStore
// ścieżkę listingu przed nawigacją. Konsumujemy żądanie dopiero gdy facety są
// załadowane (bez flasha pustego modala) i tylko gdy ścieżka wyświetlanego
// listingu (currentUrl bez query = globalResetUrl) zgadza się z żądaniem —
// żądanie dotyczące innej strony jest czyszczone jako nieaktualne.
const ui = useUIStore();

watch(
  [hasAmazzingFilters, () => ui.mobileFiltersOpenRequestPath],
  ([hasFilters, requestedPath]) => {
    if (!requestedPath || !hasFilters || !props.mobileModalFilters) {
      return;
    }
    if (!ui.consumeMobileFiltersOpenRequest(globalResetUrl.value)) {
      return;
    }
    openMobileFilterModal();
    const treeGroup = visibleAmazzingFilters.value.find(isGroupTreeLike);
    if (treeGroup) {
      // Po openMobileFilterModal() — funkcja resetuje mobileExpandedFilterGroups.
      mobileExpandedFilterGroups.value = { [treeGroup.id]: true };
    }
  },
  { immediate: true },
);

const visibleGroupCounts = ref<Record<string, number>>({});
const expandedGroupStates = ref<Record<string, boolean>>({});

function getAmazzingVisible(group: AmazzingFilterGroup) {
  return (
    visibleGroupCounts.value[getGroupKey(group)] ??
    (group.moreCutoff || INITIAL)
  );
}

function isGroupExpanded(group: AmazzingFilterGroup) {
  return Boolean(expandedGroupStates.value[getGroupKey(group)]);
}

function isAmazzingExpanded(group: AmazzingFilterGroup) {
  return isGroupExpanded(group);
}

function toggleAmazzingExpansion(group: AmazzingFilterGroup) {
  const key = getGroupKey(group);
  const isExpanded = isAmazzingExpanded(group);
  const collapsedCount = group.moreCutoff || INITIAL;
  const total = getVisibleAmazzingValues(group).length;
  visibleGroupCounts.value = {
    ...visibleGroupCounts.value,
    [key]: isExpanded ? collapsedCount : total,
  };
  expandedGroupStates.value = {
    ...expandedGroupStates.value,
    [key]: !isExpanded,
  };
}

function navigateToTarget(targetUrl: string) {
  if (!targetUrl) {
    return;
  }

  closeMobileFilterModal();
  navigateTo(targetUrl);
}

function buildAmazzingRangeUrl(template: string, from: number, to: number) {
  return template.includes(AMAZZING_PLACEHOLDER)
    ? template.replace(AMAZZING_PLACEHOLDER, `${from}-${to}`)
    : template;
}

const amazzingRangeState = ref<Record<string, { from: number; to: number }>>(
  {},
);

watch(
  amazzingFilters,
  (filters) => {
    amazzingRangeState.value = Object.fromEntries(
      filters.filter(isAmazzingSliderGroup).map((group) => [
        group.id,
        {
          from: group.range!.from,
          to: group.range!.to,
        },
      ]),
    );
  },
  { immediate: true },
);

function getAmazzingRange(group: AmazzingFilterGroup) {
  return group.range
    ? (amazzingRangeState.value[group.id] ?? {
        from: group.range.from,
        to: group.range.to,
      })
    : null;
}

function updateAmazzingPrice(
  group: AmazzingFilterGroup,
  boundary: "min" | "max",
  value: number,
) {
  const targetUrl = setAmazzingPrice(group, boundary, value);

  if (targetUrl) {
    navigateToTarget(targetUrl);
  }
}

function queueMobileAmazzingPrice(
  group: AmazzingFilterGroup,
  boundary: "min" | "max",
  value: number,
) {
  const targetUrl = setAmazzingPrice(group, boundary, value);

  if (targetUrl) {
    queueMobileFilterTarget(targetUrl);
  }
}

function setAmazzingPrice(
  group: AmazzingFilterGroup,
  boundary: "min" | "max",
  value: number,
) {
  if (!group.range) {
    return null;
  }

  const current = getAmazzingRange(group) ?? {
    from: group.range.from,
    to: group.range.to,
  };
  const nextFrom =
    boundary === "min"
      ? clampPrice(
          Math.min(value, current.to),
          group.range.min,
          group.range.max,
        )
      : current.from;
  const nextTo =
    boundary === "max"
      ? clampPrice(
          Math.max(value, current.from),
          group.range.min,
          group.range.max,
        )
      : current.to;

  amazzingRangeState.value = {
    ...amazzingRangeState.value,
    [group.id]: {
      from: nextFrom,
      to: nextTo,
    },
  };

  return buildAmazzingRangeUrl(group.range.targetUrlTemplate, nextFrom, nextTo);
}
</script>

<template>
  <aside
    class="filters"
    aria-label="Filtry produktów"
    :class="{
      'filters--loading': isInitialLoading,
      'filters--updating': isUpdating,
      'filters--mobile-modal': mobileModalFilters,
    }"
  >
    <p class="filters__heading">Filtruj według</p>

    <div
      v-if="isInitialLoading"
      class="filters__loading-overlay"
      aria-hidden="true"
    >
      <div v-for="n in 3" :key="`loading-${n}`" class="filters__loading-group">
        <span class="filters__loading-title" />
        <span
          v-for="row in 4"
          :key="`loading-${n}-${row}`"
          class="filters__loading-row"
        />
      </div>
    </div>

    <div
      v-if="isUpdating"
      class="filters__updating-overlay"
      aria-hidden="true"
    />

    <div
      class="filters__content"
      :class="{ 'filters__content--hidden': isInitialLoading }"
    >
      <div v-if="hasAmazzingFilters" class="filters__amazzing">
        <div
          v-if="mobileModalFilters"
          class="filters__mobile-panel"
          :class="{ 'filters__mobile-panel--sticky': showStickyTrigger }"
        >
          <button
            type="button"
            class="filters__mobile-trigger"
            :class="[
              { 'filters__mobile-trigger--open': isMobileFilterModalOpen },
              'filters__mobile-trigger--primary',
            ]"
            aria-haspopup="dialog"
            :aria-expanded="isMobileFilterModalOpen"
            aria-label="Pokaż panel filtrów"
            data-test="mobile-filter-panel-trigger"
            @click="openMobileFilterModal()"
          >
            <span>Pokaż filtry</span>
          </button>
        </div>

        <div class="filters__desktop-groups">
          <div
            v-if="amazzingAppliedFilters.length"
            class="filter-group filter-group--applied"
          >
            <p class="filter-group__title">Aktywne filtry</p>
            <div class="filter-applied">
              <button
                v-for="filter in amazzingAppliedFilters"
                :key="`${filter.groupId}:${filter.valueId}`"
                type="button"
                class="filter-applied__chip"
                :aria-label="`Usuń filtr: ${filter.label}`"
                @click="navigateToTarget(filter.targetUrl)"
              >
                <span>{{ filter.label }}</span>
                <span class="filter-applied__chip-x" aria-hidden="true">×</span>
              </button>
            </div>
            <button
              type="button"
              class="filter-applied__reset"
              @click="navigateToTarget(globalResetUrl)"
            >
              Zresetuj filtry
            </button>
          </div>

          <div
            v-for="group in visibleAmazzingFilters"
            :key="group.id"
            class="filter-group"
          >
            <div class="filter-group__head">
              <p class="filter-group__title">{{ group.name }}</p>
              <button
                v-if="group.resetTargetUrl && hasSelectedAmazzingValues(group)"
                type="button"
                class="filter-group__reset"
                @click="navigateToTarget(group.resetTargetUrl)"
              >
                Wyczyść
              </button>
            </div>

            <input
              v-if="
                group.hasQuickSearch ||
                shouldShowFilterSearch(getVisibleAmazzingValues(group).length)
              "
              :value="getAmazzingSearchQuery(group)"
              type="search"
              class="filter-group__search"
              placeholder="Szukaj wartości"
              :aria-label="`Szukaj wartości filtra ${group.name}`"
              @input="
                setAmazzingSearchQuery(
                  group,
                  ($event.target as HTMLInputElement).value,
                )
              "
            />

            <div
              v-if="isAmazzingSliderGroup(group) && group.range"
              class="filter-price"
            >
              <div
                class="filter-price__slider"
                :style="
                  getRangeTrackStyle(
                    group.range.min,
                    group.range.max,
                    getAmazzingRange(group)?.from ?? group.range.from,
                    getAmazzingRange(group)?.to ?? group.range.to,
                  )
                "
              >
                <span class="filter-price__track" aria-hidden="true" />
                <span class="filter-price__fill" aria-hidden="true" />
                <input
                  :value="getAmazzingRange(group)?.from ?? group.range.from"
                  type="range"
                  class="filter-price__range"
                  :aria-label="`Minimalna wartość filtra ${group.name}`"
                  :min="group.range.min"
                  :max="group.range.max"
                  @input="
                    updateAmazzingPrice(
                      group,
                      'min',
                      Number(($event.target as HTMLInputElement).value),
                    )
                  "
                />
                <input
                  :value="getAmazzingRange(group)?.to ?? group.range.to"
                  type="range"
                  class="filter-price__range"
                  :aria-label="`Maksymalna wartość filtra ${group.name}`"
                  :min="group.range.min"
                  :max="group.range.max"
                  @input="
                    updateAmazzingPrice(
                      group,
                      'max',
                      Number(($event.target as HTMLInputElement).value),
                    )
                  "
                />
              </div>
              <div class="filter-price__inputs">
                <input
                  :value="getAmazzingRange(group)?.from ?? group.range.from"
                  type="number"
                  class="filter-price__input"
                  :aria-label="`Minimalna wartość filtra ${group.name}`"
                  :min="group.range.min"
                  :max="getAmazzingRange(group)?.to ?? group.range.to"
                  @change="
                    updateAmazzingPrice(
                      group,
                      'min',
                      Number(($event.target as HTMLInputElement).value),
                    )
                  "
                />
                <span class="filter-price__sep">–</span>
                <input
                  :value="getAmazzingRange(group)?.to ?? group.range.to"
                  type="number"
                  class="filter-price__input"
                  :aria-label="`Maksymalna wartość filtra ${group.name}`"
                  :min="getAmazzingRange(group)?.from ?? group.range.from"
                  :max="group.range.max"
                  @change="
                    updateAmazzingPrice(
                      group,
                      'max',
                      Number(($event.target as HTMLInputElement).value),
                    )
                  "
                />
              </div>
            </div>

            <ul
              v-else-if="isGroupTreeLike(group)"
              class="filter-group__list filter-group__list--tree"
            >
              <li
                v-for="value in getTreeVisibleValues(group)"
                :key="`${group.id}:${value.id}`"
                class="filter-group__item filter-group__item--tree"
                :style="{ paddingInlineStart: `${value.depth * 10}px` }"
              >
                <button
                  v-if="hasVisibleChildrenInGroup(group, value)"
                  type="button"
                  class="filter-tree__chevron"
                  :class="{
                    'filter-tree__chevron--open': isTreeExpanded(group, value),
                  }"
                  :aria-expanded="isTreeExpanded(group, value)"
                  :aria-label="getTreeToggleLabel(group, value)"
                  :data-test="`filter-tree-toggle-${group.id}-${value.id}`"
                  @click.stop="toggleTreeValue(group, value)"
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.5"
                  >
                    <path d="M3 4.5L6 7.5L9 4.5" />
                  </svg>
                </button>
                <span v-else class="filter-tree__spacer" />
                <button
                  type="button"
                  class="filter-check filter-check--button"
                  :class="{ 'filter-check--disabled': value.disabled }"
                  :disabled="value.disabled"
                  @click="navigateToTarget(value.targetUrl)"
                >
                  <span
                    class="filter-check__box"
                    :class="{
                      'filter-check__box--active': value.selected,
                      'filter-check__box--partial':
                        !value.selected && hasSelectedDescendant(group, value),
                    }"
                  />
                  <span class="filter-check__label">{{ value.label }}</span>
                  <span v-if="value.count > 0" class="filter-check__count">{{
                    value.count
                  }}</span>
                </button>
              </li>
            </ul>

            <ul v-else class="filter-group__list">
              <li
                v-for="value in getRenderedAmazzingValues(group)"
                :key="`${group.id}:${value.id}`"
                class="filter-group__item"
              >
                <button
                  type="button"
                  class="filter-check filter-check--button"
                  :class="{ 'filter-check--disabled': value.disabled }"
                  :data-target-url="value.targetUrl"
                  :style="{ paddingInlineStart: `${value.depth * 14}px` }"
                  :disabled="value.disabled"
                  @click="navigateToTarget(value.targetUrl)"
                >
                  <span
                    class="filter-check__box"
                    :class="{
                      'filter-check__box--active': value.selected,
                      'filter-check__box--color':
                        group.controlType === 'color' && value.colorStyle,
                    }"
                    :style="
                      group.controlType === 'color' && value.colorStyle
                        ? value.colorStyle
                        : undefined
                    "
                  />
                  <span class="filter-check__label">{{ value.label }}</span>
                  <span class="filter-check__count">{{ value.count }}</span>
                </button>
              </li>
            </ul>

            <button
              v-if="
                !isGroupTreeLike(group) &&
                group.hasMoreToggle &&
                (hasMoreAmazzingValues(group) || isAmazzingExpanded(group))
              "
              :data-test="`toggle-group-${group.id}`"
              type="button"
              class="filter-group__more"
              @click="toggleAmazzingExpansion(group)"
            >
              {{ isAmazzingExpanded(group) ? "Pokaż mniej" : "Zobacz więcej" }}
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
              >
                <path d="M2 4L6 8L10 4" />
              </svg>
            </button>
          </div>

          <div
            v-if="
              canExpandFilters ||
              canCollapseFilters ||
              expandFiltersError ||
              isExpandingFilters
            "
            class="filter-group filter-group--expand filter-group--desktop-expand"
          >
            <button
              v-if="canExpandFilters || isExpandingFilters"
              data-test="expand-filters"
              type="button"
              class="filter-group__more filter-group__more--panel"
              :disabled="isExpandingFilters"
              @click="emit('expand-filters')"
            >
              <span
                v-if="isExpandingFilters"
                data-test="expand-filters-spinner"
                class="filter-group__more-spinner"
                aria-hidden="true"
              />
              <span>{{
                isExpandingFilters
                  ? "Ładowanie filtrów..."
                  : "Pokaż więcej filtrów"
              }}</span>
            </button>
            <button
              v-else-if="canCollapseFilters"
              data-test="collapse-filters"
              type="button"
              class="filter-group__more filter-group__more--panel"
              @click="emit('collapse-filters')"
            >
              Pokaż mniej filtrów
            </button>
            <p
              v-if="expandFiltersError"
              data-test="expand-filters-error"
              class="filters__expand-error"
            >
              {{ expandFiltersError }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <Teleport to="body">
      <Transition name="filters-mobile-modal">
        <div
          v-if="isMobileFilterModalOpen"
          class="filters-mobile-modal filters-mobile-modal--below-header"
          :style="{
            '--filters-mobile-modal-top': `${mobileModalTopOffset}px`,
          }"
          data-test="mobile-filter-modal-backdrop"
          @click.self="closeMobileFilterModal()"
        >
          <section
            class="filters-mobile-modal__dialog filters-mobile-modal__dialog--fullscreen"
            role="dialog"
            aria-modal="true"
            aria-labelledby="filters-mobile-modal-title"
          >
            <header class="filters-mobile-modal__header">
              <h2
                id="filters-mobile-modal-title"
                class="filters-mobile-modal__title"
              >
                Filtry
              </h2>
              <button
                type="button"
                class="filters-mobile-modal__close"
                aria-label="Zamknij panel filtrów"
                data-test="mobile-filter-modal-close"
                @click="closeMobileFilterModal()"
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  aria-hidden="true"
                >
                  <path d="M13.5 4.5L4.5 13.5" />
                  <path d="M4.5 4.5L13.5 13.5" />
                </svg>
              </button>
            </header>

            <div class="filters-mobile-modal__body">
              <div
                v-if="mobileAppliedFilters.length"
                class="filter-group filter-group--applied filter-group--modal filter-group--modal-compact"
              >
                <p class="filter-group__title">Aktywne filtry</p>
                <div class="filter-applied filter-applied--modal">
                  <button
                    v-for="pill in mobileAppliedFilters"
                    :key="`modal-${pill.key}`"
                    type="button"
                    class="filter-applied__chip"
                    :aria-label="`Usuń filtr: ${pill.label}`"
                    @click="removeMobileAppliedFilter(pill)"
                  >
                    <span>{{ pill.label }}</span>
                    <span class="filter-applied__chip-x" aria-hidden="true">×</span>
                  </button>
                </div>
                <button
                  type="button"
                  class="filter-applied__reset"
                  @click="navigateToTarget(globalResetUrl); closeMobileFilterModal()"
                >
                  Zresetuj filtry
                </button>
              </div>

              <div
                v-for="activeMobileFilterGroup in visibleAmazzingFilters"
                :key="`modal-group-${activeMobileFilterGroup.id}`"
                class="filter-group filter-group--modal filter-group--modal-compact"
              >
                <div class="filter-group__head filter-group__head--modal">
                  <button
                    type="button"
                    class="filters-mobile-modal__group-toggle"
                    :aria-expanded="
                      isMobileFilterGroupExpanded(activeMobileFilterGroup)
                    "
                    :data-test="`mobile-filter-group-toggle-${activeMobileFilterGroup.id}`"
                    @click="toggleMobileFilterGroup(activeMobileFilterGroup)"
                  >
                    <span class="filter-group__title">
                      {{ activeMobileFilterGroup.name }}
                    </span>
                    <svg
                      class="filters-mobile-modal__chevron"
                      :class="{
                        'filters-mobile-modal__chevron--open':
                          isMobileFilterGroupExpanded(activeMobileFilterGroup),
                      }"
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="1.5"
                      aria-hidden="true"
                    >
                      <path d="M4 6L8 10L12 6" />
                    </svg>
                  </button>
                  <button
                    v-if="
                      activeMobileFilterGroup.resetTargetUrl &&
                      hasSelectedAmazzingValues(activeMobileFilterGroup)
                    "
                    type="button"
                    class="filter-group__reset"
                    @click="
                      queueMobileFilterGroupReset(activeMobileFilterGroup)
                    "
                  >
                    Wyczyść
                  </button>
                </div>

                <div
                  v-if="isMobileFilterGroupExpanded(activeMobileFilterGroup)"
                  class="filters-mobile-modal__group-content"
                  :data-test="`mobile-filter-group-content-${activeMobileFilterGroup.id}`"
                >
                  <input
                    v-if="
                      activeMobileFilterGroup.hasQuickSearch ||
                      shouldShowFilterSearch(
                        getVisibleAmazzingValues(activeMobileFilterGroup)
                          .length,
                      )
                    "
                    :value="getAmazzingSearchQuery(activeMobileFilterGroup)"
                    type="search"
                    class="filter-group__search"
                    placeholder="Szukaj wartości"
                    :aria-label="`Szukaj wartości filtra ${activeMobileFilterGroup.name}`"
                    @input="
                      setAmazzingSearchQuery(
                        activeMobileFilterGroup,
                        ($event.target as HTMLInputElement).value,
                      )
                    "
                  />

                  <div
                    v-if="
                      isAmazzingSliderGroup(activeMobileFilterGroup) &&
                      activeMobileFilterGroup.range
                    "
                    class="filter-price"
                  >
                    <div
                      class="filter-price__slider"
                      :style="
                        getRangeTrackStyle(
                          activeMobileFilterGroup.range.min,
                          activeMobileFilterGroup.range.max,
                          getAmazzingRange(activeMobileFilterGroup)?.from ??
                            activeMobileFilterGroup.range.from,
                          getAmazzingRange(activeMobileFilterGroup)?.to ??
                            activeMobileFilterGroup.range.to,
                        )
                      "
                    >
                      <span class="filter-price__track" aria-hidden="true" />
                      <span class="filter-price__fill" aria-hidden="true" />
                      <input
                        :value="
                          getAmazzingRange(activeMobileFilterGroup)?.from ??
                          activeMobileFilterGroup.range.from
                        "
                        type="range"
                        class="filter-price__range"
                        :aria-label="`Minimalna wartość filtra ${activeMobileFilterGroup.name}`"
                        :min="activeMobileFilterGroup.range.min"
                        :max="activeMobileFilterGroup.range.max"
                        @input="
                          queueMobileAmazzingPrice(
                            activeMobileFilterGroup,
                            'min',
                            Number(($event.target as HTMLInputElement).value),
                          )
                        "
                      />
                      <input
                        :value="
                          getAmazzingRange(activeMobileFilterGroup)?.to ??
                          activeMobileFilterGroup.range.to
                        "
                        type="range"
                        class="filter-price__range"
                        :aria-label="`Maksymalna wartość filtra ${activeMobileFilterGroup.name}`"
                        :min="activeMobileFilterGroup.range.min"
                        :max="activeMobileFilterGroup.range.max"
                        @input="
                          queueMobileAmazzingPrice(
                            activeMobileFilterGroup,
                            'max',
                            Number(($event.target as HTMLInputElement).value),
                          )
                        "
                      />
                    </div>
                    <div class="filter-price__inputs">
                      <input
                        :value="
                          getAmazzingRange(activeMobileFilterGroup)?.from ??
                          activeMobileFilterGroup.range.from
                        "
                        type="number"
                        class="filter-price__input"
                        :aria-label="`Minimalna wartość filtra ${activeMobileFilterGroup.name}`"
                        :min="activeMobileFilterGroup.range.min"
                        :max="
                          getAmazzingRange(activeMobileFilterGroup)?.to ??
                          activeMobileFilterGroup.range.to
                        "
                        @change="
                          queueMobileAmazzingPrice(
                            activeMobileFilterGroup,
                            'min',
                            Number(($event.target as HTMLInputElement).value),
                          )
                        "
                      />
                      <span class="filter-price__sep">–</span>
                      <input
                        :value="
                          getAmazzingRange(activeMobileFilterGroup)?.to ??
                          activeMobileFilterGroup.range.to
                        "
                        type="number"
                        class="filter-price__input"
                        :aria-label="`Maksymalna wartość filtra ${activeMobileFilterGroup.name}`"
                        :min="
                          getAmazzingRange(activeMobileFilterGroup)?.from ??
                          activeMobileFilterGroup.range.from
                        "
                        :max="activeMobileFilterGroup.range.max"
                        @change="
                          queueMobileAmazzingPrice(
                            activeMobileFilterGroup,
                            'max',
                            Number(($event.target as HTMLInputElement).value),
                          )
                        "
                      />
                    </div>
                  </div>

                  <ul
                    v-else-if="isGroupTreeLike(activeMobileFilterGroup)"
                    class="filter-group__list filter-group__list--tree"
                  >
                    <li
                      v-for="value in getTreeVisibleValues(
                        activeMobileFilterGroup,
                      )"
                      :key="`modal-${activeMobileFilterGroup.id}:${value.id}`"
                      class="filter-group__item filter-group__item--tree"
                      :style="{ paddingInlineStart: `${value.depth * 10}px` }"
                    >
                      <button
                        v-if="
                          hasVisibleChildrenInGroup(
                            activeMobileFilterGroup,
                            value,
                          )
                        "
                        type="button"
                        class="filter-tree__chevron"
                        :class="{
                          'filter-tree__chevron--open': isTreeExpanded(
                            activeMobileFilterGroup,
                            value,
                          ),
                        }"
                        :aria-expanded="
                          isTreeExpanded(activeMobileFilterGroup, value)
                        "
                        :aria-label="
                          getTreeToggleLabel(activeMobileFilterGroup, value)
                        "
                        :data-test="`filter-tree-toggle-${activeMobileFilterGroup.id}-${value.id}`"
                        @click.stop="
                          toggleTreeValue(activeMobileFilterGroup, value)
                        "
                      >
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 12 12"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="1.5"
                        >
                          <path d="M3 4.5L6 7.5L9 4.5" />
                        </svg>
                      </button>
                      <span v-else class="filter-tree__spacer" />
                      <button
                        type="button"
                        class="filter-check filter-check--button"
                        :class="{ 'filter-check--disabled': value.disabled }"
                        :disabled="value.disabled"
                        @click="
                          queueMobileFilterValue(activeMobileFilterGroup, value)
                        "
                      >
                        <span
                          class="filter-check__box"
                          :class="{
                            'filter-check__box--active':
                              isMobileFilterValueSelected(
                                activeMobileFilterGroup,
                                value,
                              ),
                            'filter-check__box--partial':
                              !isMobileFilterValueSelected(
                                activeMobileFilterGroup,
                                value,
                              ) &&
                              hasMobileSelectedDescendant(
                                activeMobileFilterGroup,
                                value,
                              ),
                          }"
                        />
                        <span class="filter-check__label">{{
                          value.label
                        }}</span>
                        <span
                          v-if="value.count > 0"
                          class="filter-check__count"
                        >
                          {{ value.count }}
                        </span>
                      </button>
                    </li>
                  </ul>

                  <ul v-else class="filter-group__list">
                    <li
                      v-for="value in getRenderedAmazzingValues(
                        activeMobileFilterGroup,
                      )"
                      :key="`modal-${activeMobileFilterGroup.id}:${value.id}`"
                      class="filter-group__item"
                    >
                      <button
                        type="button"
                        class="filter-check filter-check--button"
                        :class="{ 'filter-check--disabled': value.disabled }"
                        :data-target-url="value.targetUrl"
                        :style="{ paddingInlineStart: `${value.depth * 14}px` }"
                        :disabled="value.disabled"
                        @click="
                          queueMobileFilterValue(activeMobileFilterGroup, value)
                        "
                      >
                        <span
                          class="filter-check__box"
                          :class="{
                            'filter-check__box--active':
                              isMobileFilterValueSelected(
                                activeMobileFilterGroup,
                                value,
                              ),
                            'filter-check__box--color':
                              activeMobileFilterGroup.controlType === 'color' &&
                              value.colorStyle,
                          }"
                          :style="
                            activeMobileFilterGroup.controlType === 'color' &&
                            value.colorStyle
                              ? value.colorStyle
                              : undefined
                          "
                        />
                        <span class="filter-check__label">{{
                          value.label
                        }}</span>
                        <span class="filter-check__count">{{
                          value.count
                        }}</span>
                      </button>
                    </li>
                  </ul>

                  <button
                    v-if="
                      !isGroupTreeLike(activeMobileFilterGroup) &&
                      activeMobileFilterGroup.hasMoreToggle &&
                      (hasMoreAmazzingValues(activeMobileFilterGroup) ||
                        isAmazzingExpanded(activeMobileFilterGroup))
                    "
                    :data-test="`toggle-group-${activeMobileFilterGroup.id}`"
                    type="button"
                    class="filter-group__more"
                    @click="toggleAmazzingExpansion(activeMobileFilterGroup)"
                  >
                    {{
                      isAmazzingExpanded(activeMobileFilterGroup)
                        ? "Pokaż mniej"
                        : "Zobacz więcej"
                    }}
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="1.5"
                    >
                      <path d="M2 4L6 8L10 4" />
                    </svg>
                  </button>
                </div>
              </div>

              <div
                v-if="
                  canExpandFilters ||
                  canCollapseFilters ||
                  expandFiltersError ||
                  isExpandingFilters
                "
                class="filter-group filter-group--expand filter-group--modal-expand"
              >
                <button
                  v-if="canExpandFilters || isExpandingFilters"
                  data-test="mobile-expand-filters"
                  type="button"
                  class="filter-group__more filter-group__more--panel"
                  :disabled="isExpandingFilters"
                  @click="emit('expand-filters')"
                >
                  <span
                    v-if="isExpandingFilters"
                    data-test="mobile-expand-filters-spinner"
                    class="filter-group__more-spinner"
                    aria-hidden="true"
                  />
                  <span>{{
                    isExpandingFilters
                      ? "Ładowanie filtrów..."
                      : "Pokaż więcej filtrów"
                  }}</span>
                </button>
                <button
                  v-else-if="canCollapseFilters"
                  data-test="mobile-collapse-filters"
                  type="button"
                  class="filter-group__more filter-group__more--panel"
                  @click="emit('collapse-filters')"
                >
                  Pokaż mniej filtrów
                </button>
                <p
                  v-if="expandFiltersError"
                  data-test="mobile-expand-filters-error"
                  class="filters__expand-error"
                >
                  {{ expandFiltersError }}
                </p>
              </div>
            </div>

            <footer class="filters-mobile-modal__footer">
              <button
                type="button"
                class="filters-mobile-modal__footer-close"
                data-test="mobile-filter-modal-footer-close"
                :data-pending-target-url="mobilePendingTargetUrl ?? ''"
                @click="applyMobileFilters()"
              >
                Zastosuj
              </button>
            </footer>
          </section>
        </div>
      </Transition>
    </Teleport>
  </aside>
</template>

<style scoped>
.filters {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0;
}

.filters--loading {
  opacity: 0.72;
}

.filters--updating {
  opacity: 0.58;
  pointer-events: none;
}

.filters__heading {
  margin: 0 0 20px;
  font-size: 1.75rem;
  font-weight: var(--font-weight-normal);
  color: var(--color-black);
  line-height: 1;
}

.filters__content--hidden {
  visibility: hidden;
}

.filters__mobile-panel {
  display: none;
}

.filters__desktop-groups {
  display: block;
}

.filters__mobile-trigger {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  min-height: 44px;
  border: 1px solid var(--btn-primary-bg, var(--color-black));
  border-radius: var(--btn-pill-radius);
  background: var(--btn-primary-bg, var(--color-black));
  padding: 0 18px;
  font-family: var(--font-body);
  font-size: 0.875rem;
  font-weight: var(--font-weight-normal);
  color: var(--btn-primary-text, var(--color-white));
  letter-spacing: 0.04em;
  line-height: 1;
  text-align: center;
  cursor: pointer;
  transition:
    background var(--transition-fast),
    color var(--transition-fast),
    border-color var(--transition-fast);
}

.filters__mobile-trigger:hover,
.filters__mobile-trigger:focus-visible {
  background: var(--color-white);
  color: var(--color-black);
}

.filters-mobile-modal {
  --filters-mobile-modal-top: 102px;
  position: fixed;
  top: var(--filters-mobile-modal-top);
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 90;
  display: flex;
  align-items: stretch;
  justify-content: center;
  background: rgba(21, 21, 21, 0.42);
}

.filters-mobile-modal__dialog {
  width: 100%;
  max-height: min(82dvh, 680px);
  display: flex;
  flex-direction: column;
  border-radius: 8px 8px 0 0;
  background: var(--color-white);
  color: var(--color-black);
  overflow: hidden;
}

.filters-mobile-modal__dialog--fullscreen {
  height: 100%;
  max-height: none;
  border-radius: 0;
}

.filters-mobile-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border-bottom: 1px solid var(--color-border);
  padding: 5px 20px;
}

.filters-mobile-modal__title {
  margin: 0;
  font-size: 1rem;
  font-weight: var(--font-weight-normal);
  line-height: 1.2;
}

.filters-mobile-modal__group-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-width: 0;
  flex: 1;
  min-height: 36px;
  border: none;
  background: none;
  padding: 0;
  font-family: var(--font-body);
  color: var(--color-black);
  cursor: pointer;
  text-align: left;
}

.filters-mobile-modal__group-toggle .filter-group__title {
  margin: 0;
}

.filters-mobile-modal__chevron {
  flex: 0 0 16px;
  color: var(--color-muted);
  transition: transform 0.18s ease;
}

.filters-mobile-modal__chevron--open {
  transform: rotate(180deg);
}

.filters-mobile-modal__close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  flex: 0 0 36px;
  border: 1px solid var(--color-border);
  border-radius: 999px;
  background: var(--color-white);
  color: var(--color-black);
  cursor: pointer;
}

.filters-mobile-modal__body {
  flex: 1;
  overflow: auto;
  padding: 0 20px 24px;
  -webkit-overflow-scrolling: touch;
}

.filters-mobile-modal__group-content {
  display: flex;
  flex-direction: column;
  padding-top: 10px;
}

.filters-mobile-modal__footer {
  border-top: 1px solid var(--color-border);
  padding: 14px 20px calc(14px + env(safe-area-inset-bottom));
  background: var(--color-white);
}

.filters-mobile-modal__footer-close {
  width: 100%;
  min-height: 44px;
  border: 1px solid var(--color-black);
  border-radius: var(--btn-pill-radius);
  background: var(--color-black);
  color: var(--color-white);
  font-family: var(--font-body);
  font-size: 0.9375rem;
  font-weight: var(--font-weight-normal);
  cursor: pointer;
}

.filter-group__head--modal {
  margin-bottom: 0;
}

.filter-group--modal {
  padding: 12px 0;
}

.filter-group--modal-compact {
  padding-block: 12px;
}

.filter-applied--modal {
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
}

.filters-mobile-modal-enter-active,
.filters-mobile-modal-leave-active {
  transition: opacity 0.18s ease;
}

.filters-mobile-modal-enter-active .filters-mobile-modal__dialog,
.filters-mobile-modal-leave-active .filters-mobile-modal__dialog {
  transition: transform 0.18s ease;
}

.filters-mobile-modal-enter-from,
.filters-mobile-modal-leave-to {
  opacity: 0;
}

.filters-mobile-modal-enter-from .filters-mobile-modal__dialog,
.filters-mobile-modal-leave-to .filters-mobile-modal__dialog {
  transform: translateY(16px);
}

.filters__loading-overlay {
  position: absolute;
  inset: 28px 0 0;
  z-index: 2;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-bottom: 12px;
  pointer-events: none;
}

.filters__updating-overlay {
  position: absolute;
  inset: 28px 0 0;
  z-index: 2;
  background: rgba(255, 255, 255, 0.28);
  pointer-events: none;
}

.filters__loading-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
  border-top: 1px solid var(--color-border);
  padding: 16px 0;
}

.filters__loading-title,
.filters__loading-row {
  display: block;
  border-radius: 999px;
  background: linear-gradient(
    90deg,
    rgba(0, 0, 0, 0.06),
    rgba(0, 0, 0, 0.12),
    rgba(0, 0, 0, 0.06)
  );
  background-size: 200% 100%;
  animation: filters-loading 1.1s linear infinite;
}

.filters__loading-title {
  width: 42%;
  height: 12px;
}

.filters__loading-row {
  width: 100%;
  height: 18px;
}

/* ── Group ── */
.filter-group {
  border-top: 1px solid var(--color-border);
  padding: 16px 0;
}

.filter-group--applied {
  padding-top: 0;
}

.filter-group__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.filter-group__title {
  margin: 0 0 12px;
  font-size: 0.875rem;
  font-weight: var(--font-weight-normal);
  color: var(--color-black);
  line-height: 1;
}

.filter-group__head .filter-group__title {
  margin-bottom: 0;
}

.filter-group__reset {
  border: none;
  background: none;
  padding: 0;
  font-size: 0.75rem;
  color: var(--color-muted);
  cursor: pointer;
}

.filter-group__search {
  width: 100%;
  height: 38px;
  margin: 0 0 12px;
  border: 1px solid var(--color-border-md);
  padding: 0 12px;
  font-family: var(--font-body);
  font-size: 0.875rem;
  font-weight: var(--font-weight-light);
  color: var(--color-black);
  background: var(--color-white);
}

.filter-group__search::placeholder {
  color: var(--color-muted);
}

.filter-group__search:focus {
  outline: none;
  border-color: var(--color-black);
}

.filter-group__list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.filter-group__list--tree {
  gap: 3px;
}

.filter-group__item {
  display: flex;
  align-items: center;
}

/* ── Checkbox row ── */
.filter-check {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  width: 100%;
}

.filter-check--button {
  border: none;
  background: none;
  padding: 0;
  text-align: left;
}

.filter-check--disabled {
  cursor: default;
  opacity: 0.5;
}

.filter-check__input {
  display: none;
}

.filter-check__box {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 1px solid var(--color-border-md);
  background: var(--color-white);
  flex-shrink: 0;
  position: relative;
  transition:
    background var(--transition-fast),
    border-color var(--transition-fast);
}

.filter-check__input:checked + .filter-check__box {
  background: var(--color-black);
  border-color: var(--color-black);
}

.filter-check__box--active {
  background: var(--color-black);
  border-color: var(--color-black);
}

.filter-check__input:checked + .filter-check__box::after {
  content: "";
  position: absolute;
  left: 4px;
  top: 1px;
  width: 6px;
  height: 10px;
  border: 2px solid var(--color-white);
  border-top: none;
  border-left: none;
  transform: rotate(45deg);
}

.filter-check__box--active::after {
  content: "";
  position: absolute;
  left: 4px;
  top: 1px;
  width: 6px;
  height: 10px;
  border: 2px solid var(--color-white);
  border-top: none;
  border-left: none;
  transform: rotate(45deg);
}

.filter-check__box--color {
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
}

.filter-check__label {
  font-size: 0.875rem;
  font-weight: var(--font-weight-light);
  color: var(--color-black);
  line-height: 1;
  flex: 1;
}

.filter-check__count {
  font-size: 0.8125rem;
  font-weight: var(--font-weight-light);
  color: var(--color-muted);
  line-height: 1;
  flex-shrink: 0;
}

/* ── Category navigation link ── */
.filter-cat-link {
  display: flex;
  align-items: center;
  width: 100%;
  text-decoration: none;
  gap: 10px;
  padding: 2px 0;
  transition: color var(--transition-fast);
}

.filter-cat-link:hover .filter-cat-link__label {
  color: var(--color-black);
}

.filter-cat-link__label {
  font-size: 0.875rem;
  font-weight: var(--font-weight-light);
  color: var(--color-muted);
  line-height: 1;
  flex: 1;
  transition: color var(--transition-fast);
}

/* ── Load more button ── */
.filter-group__more {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 12px;
  background: none;
  border: 1px solid var(--color-black);
  border-radius: var(--btn-pill-radius);
  padding: 10px 18px;
  font-family: var(--font-body);
  font-size: 1rem;
  font-weight: var(--font-weight-light);
  color: var(--color-black);
  cursor: pointer;
  width: 100%;
  justify-content: center;
  transition: background var(--transition-fast);
}

@keyframes filter-spinner-rotate {
  to {
    transform: rotate(360deg);
  }
}

.filter-group__more-spinner {
  width: 1em;
  height: 1em;
  flex: 0 0 1em;
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: 999px;
  animation: filter-spinner-rotate 0.75s linear infinite;
}

.filter-group__more:hover {
  background: var(--color-bg);
}

.filter-group__more--panel:disabled {
  cursor: wait;
  opacity: 1;
  background: var(--color-bg);
}

.filters__expand-error {
  margin: 10px 0 0;
  font-size: 0.8125rem;
  font-weight: var(--font-weight-light);
  color: var(--color-danger, #b42318);
  line-height: 1.4;
}

.filter-applied {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.filter-applied__chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  min-height: 36px;
  border: 1px solid var(--color-border-md);
  border-radius: 999px;
  background: var(--color-white);
  padding: 6px 8px 6px 14px;
  font-size: 0.8125rem;
  line-height: 1.2;
  color: var(--color-black);
  cursor: pointer;
  transition:
    background var(--transition-fast),
    border-color var(--transition-fast),
    color var(--transition-fast);
}

.filter-applied__chip > span:first-child {
  /* min-width: 0 lets the flex item shrink so the ellipsis actually fires
     instead of a long label pushing the pill past the chip's max-width. */
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.filter-applied__chip:hover,
.filter-applied__chip:focus-visible {
  border-color: var(--color-black);
  background: var(--color-bg);
}

.filter-applied__chip-x {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  border-radius: 999px;
  background: var(--color-bg, #f2f2f2);
  color: var(--color-muted);
  font-size: 0.9375rem;
  line-height: 1;
  transition:
    background var(--transition-fast),
    color var(--transition-fast);
}

.filter-applied__chip:hover .filter-applied__chip-x,
.filter-applied__chip:focus-visible .filter-applied__chip-x {
  background: var(--color-black);
  color: var(--color-white);
}

.filter-applied__reset {
  display: inline-block;
  margin-top: 10px;
  padding: 0;
  border: 0;
  background: transparent;
  font: inherit;
  font-size: 0.8125rem;
  font-weight: var(--font-weight-light);
  color: var(--color-muted, #6b6b6b);
  text-decoration: underline;
  text-underline-offset: 3px;
  cursor: pointer;
}

.filter-applied__reset:hover {
  color: var(--color-black);
}

/* ── Scroll sentinel (invisible trigger) ── */
.filter-sentinel {
  height: 1px;
  margin-top: 4px;
}

/* ── Price ── */
.filter-price {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.filter-price__slider {
  --range-start: 0%;
  --range-end: 100%;
  position: relative;
  height: 24px;
}

.filter-price__track,
.filter-price__fill {
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  height: 4px;
  transform: translateY(-50%);
  border-radius: 999px;
  pointer-events: none;
}

.filter-price__track {
  background: var(--color-border);
}

.filter-price__fill {
  left: var(--range-start);
  right: calc(100% - var(--range-end));
  background: var(--color-black);
}

.filter-price__range {
  position: absolute;
  inset: 0;
  width: 100%;
  margin: 0;
  background: transparent;
  accent-color: var(--color-black);
  cursor: pointer;
}

.filter-price__inputs {
  display: flex;
  align-items: center;
  gap: 8px;
}

.filter-price__input {
  width: 80px;
  height: 36px;
  border: 1px solid var(--color-border-md);
  padding: 0 8px;
  font-family: var(--font-body);
  font-size: 0.875rem;
  font-weight: var(--font-weight-light);
  color: var(--color-black);
  background: var(--color-white);
  text-align: center;
}

.filter-price__input:focus {
  outline: none;
  border-color: var(--color-black);
}

.filter-price__sep {
  font-size: 0.875rem;
  color: var(--color-muted);
}

@keyframes filters-loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* ── Tree hierarchy ── */
.filter-group__item--tree {
  position: relative;
  gap: 4px;
  min-height: 26px;
  padding-inline-start: 0; /* depth indent is set via inline style */
}

.filter-group__item--tree::before {
  content: "";
  position: absolute;
  left: 10px;
  top: 6px;
  bottom: 6px;
  width: 1px;
  background: var(--color-border);
  opacity: 0.7;
}

.filter-group__item--tree:hover .filter-check__label,
.filter-group__item--tree:focus-within .filter-check__label {
  color: var(--color-black);
}

.filter-group__item--tree:hover
  .filter-check:not(.filter-check--disabled)
  .filter-check__box,
.filter-group__item--tree:focus-within
  .filter-check:not(.filter-check--disabled)
  .filter-check__box {
  border-color: var(--color-black);
}

.filter-group__item--tree .filter-check--button {
  min-height: 26px;
  border-radius: 999px;
  padding: 2px 6px 2px 1px;
  transition: background var(--transition-fast);
}

.filter-group__item--tree .filter-check--button:hover,
.filter-group__item--tree .filter-check--button:focus-visible {
  background: var(--color-bg);
}

.filter-group__item--tree .filter-check__box--active + .filter-check__label {
  color: var(--color-black);
}

.filter-check__box--partial {
  background: var(--color-black);
  border-color: var(--color-black);
}

.filter-check__box--partial::after {
  content: "";
  position: absolute;
  left: 3px;
  right: 3px;
  top: 50%;
  height: 2px;
  background: var(--color-white);
  transform: translateY(-50%);
  width: auto;
  border: none;
}

.filter-group__item--tree .filter-check__box {
  width: 14px;
  height: 14px;
}

.filter-group__item--tree .filter-check__box--active::after {
  left: 3px;
  top: 1px;
  width: 5px;
  height: 8px;
}

.filter-tree__chevron {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  border: 1px solid transparent;
  border-radius: 999px;
  background: var(--color-white);
  padding: 0;
  cursor: pointer;
  color: var(--color-muted);
  transition:
    color var(--transition-fast),
    border-color var(--transition-fast),
    background var(--transition-fast);
  z-index: 1;
}

.filter-tree__chevron:hover,
.filter-tree__chevron:focus-visible {
  color: var(--color-black);
  border-color: var(--color-border-md);
  background: var(--color-bg);
}

.filter-tree__chevron svg {
  transition: transform 0.18s ease;
}

.filter-tree__chevron--open svg {
  transform: rotate(180deg);
}

.filter-tree__spacer {
  display: inline-block;
  width: 24px;
  flex-shrink: 0;
}

@media (max-width: 960px) {
  /* Rezerwacja miejsca na trigger „Pokaż filtry" (44 px). Filtry doładowują się
     KLIENTOWO (primary + stable ssr:false, by nie wysyłać ~2 MB drzewa facetów
     w SSR HTML). Bez tej rezerwacji, przy bezpośrednim wejściu na kategorię, aside
     ma 0 px na pierwszym paincie (hasAmazzingFilters=false póki dane nie dojdą),
     a po hydracji trigger wskakuje i spycha siatkę produktów w dół → layout shift
     (CLS). min-height utrzymuje stałą wysokość aside we wszystkich stanach
     (SSR pusty → skeleton → załadowane), więc siatka nie skacze. `filters__content`
     jest 0 px gdy brak danych; gdy przyjdą, panel (44 px) wypełnia zarezerwowane
     min-height — bez wzrostu, bez CLS. */
  .filters--mobile-modal {
    min-height: 44px;
  }

  /* Skeleton filtrów (filters__loading-overlay) jest desktopowy (3 grupy list) i
     position:absolute — na mobile pokazujemy tylko trigger, więc overlay by tylko
     migał nad zarezerwowanym miejscem. Ukryj go tutaj. */
  .filters--mobile-modal .filters__loading-overlay {
    display: none;
  }

  .filters--mobile-modal .filters__mobile-panel {
    display: block;
  }

  /* Roomier tap targets for the active-filter pills inside the mobile modal. */
  .filter-applied--modal .filter-applied__chip {
    min-height: 40px;
    gap: 8px;
  }

  .filter-applied--modal .filter-applied__chip-x {
    width: 22px;
    height: 22px;
  }

  .filters--mobile-modal .filters__desktop-groups {
    display: none;
  }

  .filters--mobile-modal .filters__heading {
    display: none;
  }

  .filter-group__list--tree {
    gap: 6px;
  }

  .filter-group__item--tree {
    min-height: 36px;
  }

  .filter-group__item--tree .filter-check--button {
    min-height: 36px;
    padding: 4px 8px;
  }

  .filter-tree__chevron {
    width: 36px;
    height: 36px;
  }

  .filter-tree__spacer {
    width: 36px;
  }

  .filters__mobile-panel--sticky {
    position: fixed;
    bottom: 24px;
    left: 16px;
    right: 16px;
    z-index: 50;
    animation: sticky-trigger-in 0.2s ease;
  }

  .filter-group__head {
    margin-bottom: 0;
  }

  .filter-group {
    padding: 0;
  }

  @keyframes sticky-trigger-in {
    from {
      opacity: 0;
      transform: translateY(8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
}
</style>
